import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";

const NAV = [
  { label: "Manifest", href: "#manifest" },
  { label: "Dating", href: "#dating" },
  { label: "Bartending", href: "#bartending" },
  { label: "Knowledge", href: "#knowledge" },
  { label: "Therapy", href: "#therapy" },
  { label: "Book", href: "#book" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || open || loc.pathname !== "/";

  return (
    <>
      <motion.header
        data-testid="site-nav"
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1.1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-500 ${
          solid ? "bg-[#080808]/90 backdrop-blur-xl border-b border-[#2C2C2C]" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-3 group">
            <span className="font-display text-2xl md:text-3xl tracking-tighter">Trevor</span>
            <span className="hidden md:inline font-mono text-[10px] tracking-[0.3em] text-white/40 group-hover:text-[#C67022] transition-colors">
              /  EST. MMXXVI
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-10">
            {NAV.map((item) => (
              <a
                key={item.label}
                href={item.href}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className="font-mono text-[11px] tracking-[0.25em] uppercase text-white/70 hover:text-[#C67022] transition-colors"
              >
                {item.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <a
              href="#book"
              data-testid="nav-cta-book"
              className="hidden md:inline-flex items-center gap-2 border border-[#C67022] text-[#C67022] hover:bg-[#C67022] hover:text-[#080808] font-mono text-[11px] tracking-[0.25em] uppercase px-5 py-3 transition-colors"
            >
              Reserve <span className="text-base leading-none">→</span>
            </a>
            <button
              data-testid="nav-menu-toggle"
              aria-label="Toggle menu"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden w-10 h-10 border border-white/20 flex items-center justify-center"
            >
              <div className="w-4 flex flex-col gap-1.5">
                <span className={`h-px bg-white transition-transform ${open ? "translate-y-[3px] rotate-45" : ""}`} />
                <span className={`h-px bg-white transition-transform ${open ? "-translate-y-[3px] -rotate-45" : ""}`} />
              </div>
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-30 bg-[#080808] lg:hidden pt-24 px-8"
          >
            <div className="flex flex-col gap-6">
              {NAV.map((item, i) => (
                <motion.a
                  key={item.label}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="font-display text-5xl tracking-tighter hover:text-[#C67022] transition-colors"
                >
                  {item.label}
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
